import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { format } from "date-fns";
import {
  MoreHorizontal, 
  Trash2, 
  Share2, 
  Play, 
  Pause, 
  Users, 
  Eye, 
  ExternalLink,
  UserCheck,
  ShieldCheck,
  Crown,
  Copy
} from "lucide-react";
import { useNavigate } from "react-router-dom";
interface TestLike {
  id: string;
  title: string;
  description: string;
  location: string;
  created_at: string;
  is_active?: boolean;
  status: string;
  response_count?: number;
}
import { getPermissionLabel } from "@/utils/collaboration";

interface EnterpriseTestTableProps {
  tests: TestLike[];
  selectedTests: string[];
  onTestSelect: (testId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  visibleColumns: string[];
  onDeleteTest: (testId: string) => void;
  onInviteCandidates?: (testId: string) => void;
  onToggleStatus: (testId: string, currentStatus: boolean) => void;
  onInviteCollaborator: (testId: string) => void;
  onDuplicateTest: (testId: string) => void;
  isLoading?: boolean;
}

export const EnterpriseTestTable = ({
  tests,
  selectedTests,
  onTestSelect,
  onSelectAll,
  visibleColumns,
  onDeleteTest,
  onInviteCandidates,
  onToggleStatus,
  onInviteCollaborator,
  onDuplicateTest,
  isLoading = false
}: EnterpriseTestTableProps) => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<string>("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedTests = [...tests].sort((a, b) => {
    let aValue: any = a[sortField as keyof TestLike];
    let bValue: any = b[sortField as keyof TestLike];
    
    if (sortField === "created_at") {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const handleRowClick = (testId: string) => {
    navigate(`/test/${testId}`);
  };

  const allSelected = tests.length > 0 && selectedTests.length === tests.length;
  const someSelected = selectedTests.length > 0 && selectedTests.length < tests.length;

  if (tests.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="max-w-md mx-auto">
            <h3 className="text-lg font-semibold text-foreground mb-2">No tests found</h3>
            <p className="text-muted-foreground mb-4">
              No tests match your current filters. Try adjusting your search or filter criteria.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="bg-white border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="bg-muted/10 border-b border-border">
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-12 pl-6 py-4">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(checked) => onSelectAll(!!checked)}
                  aria-label="Select all tests"
                />
              </TableHead>
                {visibleColumns.includes("title") && (
                  <TableHead 
                    className="font-semibold cursor-pointer hover:bg-muted/20 transition-colors py-4"
                    onClick={() => handleSort("title")}
                  >
                    Test Name
                    {sortField === "title" && (
                      <span className="ml-1 text-xs">
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </TableHead>
                )}
                {visibleColumns.includes("responses") && (
                  <TableHead 
                    className="font-semibold cursor-pointer hover:bg-muted/20 transition-colors py-4"
                    onClick={() => handleSort("response_count")}
                  >
                    Responses
                    {sortField === "response_count" && (
                      <span className="ml-1 text-xs">
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </TableHead>
                )}
                {visibleColumns.includes("location") && (
                  <TableHead className="font-semibold py-4">Location</TableHead>
                )}
                {visibleColumns.includes("created") && (
                  <TableHead 
                    className="font-semibold cursor-pointer hover:bg-muted/20 transition-colors py-4"
                    onClick={() => handleSort("created_at")}
                  >
                    Created
                    {sortField === "created_at" && (
                      <span className="ml-1 text-xs">
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </TableHead>
                )}
                {visibleColumns.includes("active") && (
                  <TableHead className="font-semibold py-4">Active</TableHead>
                )}
                {visibleColumns.includes("actions") && (
                  <TableHead className="font-semibold text-right pr-6 w-24 py-4">Actions</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedTests.map((test, index) => (
                <TableRow
                  key={test.id}
                  className={`
                    cursor-pointer transition-all duration-150 hover:bg-muted/10 border-b border-border/50
                    ${selectedTests.includes(test.id) ? "bg-accent/10" : ""}
                  `}
                  onClick={() => handleRowClick(test.id)}
                >
                  <TableCell className="pl-6 py-4">
                    <Checkbox
                      checked={selectedTests.includes(test.id)}
                      onCheckedChange={(checked) => onTestSelect(test.id, !!checked)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select test ${test.title}`}
                    />
                  </TableCell>
                  
                  {visibleColumns.includes("title") && (
                    <TableCell className="font-medium max-w-xs py-4">
                      <div className="flex items-center gap-2">
                        <div className="truncate">
                          {test.title}
                        </div>
                        {(test as any).isCollaborated && (
                          <TooltipProvider>
                            <div className="flex gap-1 flex-shrink-0 items-center">
                              <Tooltip>
                                <TooltipTrigger>
                                  <UserCheck className="h-4 w-4 text-blue-600" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Collaborator</p>
                                </TooltipContent>
                              </Tooltip>
                              {(test as any).collaborationPermission === 'view_responses' && (
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Eye className="h-3.5 w-3.5 text-gray-500" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>View Only</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {(test as any).collaborationPermission === 'manage_recruitment' && (
                                <Tooltip>
                                  <TooltipTrigger>
                                    <ShieldCheck className="h-3.5 w-3.5 text-orange-500" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Manage Recruitment</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {(test as any).collaborationPermission === 'full_access' && (
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Crown className="h-3.5 w-3.5 text-purple-500" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Full Access</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </TooltipProvider>
                        )}
                      </div>
                    </TableCell>
                  )}
                  
                  {visibleColumns.includes("responses") && (
                    <TableCell className="py-4">
                      <span className="font-medium">{test.response_count || 0}</span>
                    </TableCell>
                  )}
                  
                  {visibleColumns.includes("location") && (
                    <TableCell className="text-muted-foreground py-4">
                      {test.location || "Remote"}
                    </TableCell>
                  )}
                  
                  {visibleColumns.includes("created") && (
                    <TableCell className="text-muted-foreground py-4">
                      {format(new Date(test.created_at), "MMM d, yyyy")}
                    </TableCell>
                  )}
                  
                  {visibleColumns.includes("active") && (
                    <TableCell className="py-4">
                      <Badge 
                        variant="outline" 
                        className={
                          test.is_active === true
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-100 dark:border-emerald-800"
                            : "bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-900/20 dark:text-gray-100 dark:border-gray-800"
                        }
                      >
                        {test.is_active === true ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                  )}
                  
                  {visibleColumns.includes("actions") && (
                    <TableCell className="text-right pr-6 py-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => navigate(`/test/${test.id}`)}>
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(`/test/${test.id}`, '_blank')}>
                            <ExternalLink className="mr-2 h-4 w-4" />
                            Open in New Tab
                          </DropdownMenuItem>
                          {onInviteCandidates && (
                            <DropdownMenuItem onClick={() => onInviteCandidates(test.id)}>
                              <Share2 className="mr-2 h-4 w-4" />
                              Share Test
                            </DropdownMenuItem>
                          )}
                          {!(test as any).isCollaborated && (
                            <>
                              <DropdownMenuItem onClick={() => onInviteCollaborator(test.id)}>
                                <Users className="mr-2 h-4 w-4" />
                                Invite Collaborator
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => onDuplicateTest(test.id)}>
                                <Copy className="mr-2 h-4 w-4" />
                                Duplicate Test
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuItem onClick={() => onToggleStatus(test.id, test.is_active ?? false)}>
                            {test.is_active === true ? (
                              <>
                                <Pause className="mr-2 h-4 w-4" />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <Play className="mr-2 h-4 w-4" />
                                Activate
                              </>
                            )}
                          </DropdownMenuItem>
                          {!(test as any).isCollaborated && (
                            <DropdownMenuItem 
                              onClick={() => onDeleteTest(test.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete Test
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
    </div>
  );
};